import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, OnChanges, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ignore422 } from '@scripts/utils/utils';
import { ModalService } from '@src/shared/modal/modal.service';
import { max } from 'lodash-es';
import { Editable, Hidden, SimpleChanges } from '../../../../scripts/utils/types';
import { InstreamCreativeFormLayoutProfile } from '../../../../scripts/woo_components.module/instream_creative_form/instream-creative-form.component';
import { CampaignService } from '../../../../scripts/woo_services.module/CampaignService';
import {
  FetchPlacementsJobResult,
  MrmCreativeInstance,
  Placement,
  PlacementService,
} from '../../../../scripts/woo_services.module/PlacementService';
import { TranslationService } from '../../../../scripts/woo_services.module/TranslationService';
import { DotLoader } from '../../../../src/shared/feedback/loader/dot-loader.component';
import { WooComponentsModule } from '../../../woo_components.module/module';
import { CreativeData, CreativeService } from '../../../woo_services.module/CreativeService';
import { Campaign, Creative, DatePeriod, ProductFormatConditions } from '../../../woo_services.module/shared-types';

@Component({
  selector: 'add-creative-to-ongoing-campaign',
  imports: [CommonModule, WooComponentsModule, DotLoader, FormsModule],
  templateUrl: './add-creative-to-ongoing-campaign.component.html',
  styleUrls: ['./add-creative-to-ongoing-campaign.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCreativeToOngoingCampaign implements OnChanges {
  readonly instreamFormProfile: InstreamCreativeFormLayoutProfile = {
    actionButtons: Hidden,
    agAffectInventoryInfo: Hidden,
    agDisabledInfo: Hidden,
    heading: Hidden,
    length: Editable,
  };

  campaign = input<Campaign>();
  onAbort = output<void>();

  creativeData: CreativeData;
  creativeDataValid = signal<boolean>(true);
  oldCreatives = signal<Creative[]>([]);
  creativeLengths = signal<number[]>([]);
  productFormatConditions = signal<ProductFormatConditions>({ onlyShortFormat: false });
  hasAgCreative = signal<boolean>(false);

  placements = signal<Placement[]>([]);
  placementCards = signal<PlacementCard[]>([]);
  pickedPlacements = signal<[]>([]);

  period: DatePeriod = { start: new Date(), end: new Date() };

  loadingPlacements = signal<boolean>(true);
  warnPlacementsCleared = signal<boolean>(false);

  get allowSubmit(): boolean {
    return this.creativeData && this.creativeDataValid() && this.getPickedPlacementIds().length > 0;
  }

  constructor(
    private creativeService: CreativeService,
    private campaignService: CampaignService,
    private placementService: PlacementService,
    private modalService: ModalService,
    private translationService: TranslationService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.campaign) {
      this.creativeLengths.set(this.determineValidCreativeLengths());
    }

    this.placementService
      .getCampaignPlacements(this.campaign().id)
      .then((jobResult: FetchPlacementsJobResult) => {
        this.placements.set(jobResult.placements);
        this.buildPlacementCards();
      })
      .catch((error: any) => {
        this.modalService.openError(this.translationService.convertError(error.error));
      })
      .finally(() => {
        this.loadingPlacements.set(false);
      });
  }

  changeCreativeData(event: Creative): void {
    if (this.creativeData && this.creativeData.length !== event.length) {
      this.warnPlacementSelectionCleared();
      this.clearAllPickedPlacementsAndRemoveAddedInstances();
    }
    this.creativeData = event;
  }

  warnPlacementSelectionCleared(): void {
    const pickedPlacements = this.getPickedPlacementIds();
    this.warnPlacementsCleared.set(pickedPlacements.length > 0);
  }

  clearAllPickedPlacementsAndRemoveAddedInstances(): void {
    this.placementCards().map((placementCard) => {
      placementCard.picked = false;
      placementCard.ad_unit_nodes.map((ad_unit_node) => {
        ad_unit_node.creative_instances = ad_unit_node.creative_instances.filter((creative_instance) => {
          return !creative_instance.added;
        });
      });
    });
  }

  async ngOnChanges(changes: SimpleChanges<AddCreativeToOngoingCampaign>): Promise<void> {
    if (changes.campaign && this.campaign) {
      this.oldCreatives.set(this.campaign().targetings.flatMap((t) => t.creatives));
      this.hasAgCreative.set(this.creativeService.checkForAgCreatives(this.oldCreatives()));
    }
  }

  async addCreative(overrideWarning: boolean = false): Promise<void> {
    const loaderModal = this.modalService.openLoader('Lägger till film');
    return this.campaignService
      .addCreativeToOngoingCampaign(this.campaign().id, this.creativeData, this.filterAddedInstances(), overrideWarning)
      .then((_response) => {
        this.modalService.openSuccess('Filmen har lagts till på kampanjen.');
        this.onAbort.emit();
      })
      .catch((err: any) => {
        ignore422(err);
        if (err.error.warning) {
          this.convertWarning(err.error.error_code, []);
        } else {
          this.modalService.openError(this.translationService.convertError(err.error.error));
        }
      })
      .finally(() => {
        loaderModal.close();
      });
  }

  convertWarning(warning: string, args: unknown[] = []): void {
    const confirmModal = this.modalService.openConfirm({
      title: this.translationService.convertWarning(warning, []).title,
      warnings: [
        {
          title: 'Varning!',
          texts: [this.translationService.convertWarning(warning, args).message],
        },
      ],
      confirmLabel: 'Jag förstår riskerna och vill fortsätta!',
      cancelLabel: 'Avbryt',
    });

    confirmModal.then((response) => {
      if (response.confirm) {
        this.addCreative(true).catch((err: any) => {
          this.modalService.openError(this.translationService.convertError(err.error.error));
        });
      }
    });
  }

  validForVideoLength(placement: PlacementCard): boolean {
    if (typeof this.creativeData.length !== 'number') return true;
    const maxCreativeLength = max(
      placement.ad_unit_nodes.flatMap((ad_unit_node) => {
        return ad_unit_node.creative_instances.flatMap((creative_instance) => {
          return creative_instance.length;
        });
      }),
    );
    return this.creativeData.length > maxCreativeLength;
  }

  addCreativeToPlacement(placement: PlacementCard): void {
    if (placement.picked) {
      placement.ad_unit_nodes.map((ad_unit_node) => {
        const creative_instance = {
          custom_name: this.creativeData.custom_name,
          video_code: this.creativeData.video_code,
          length: this.creativeData.length,
          creative_external_id: '',
          start: placement.start_date,
          end: placement.end_date,
          added: true,
        };
        ad_unit_node.creative_instances.push(creative_instance);
      });
    } else {
      placement.ad_unit_nodes.map((ad_unit_node) => {
        ad_unit_node.creative_instances.pop();
      });
    }
    return;
  }

  updateInstanceDates(event: { start: Date; end: Date }, creativeInstance: MrmCreativeInstance): void {
    creativeInstance.start = event.start;
    creativeInstance.end = event.end;
  }

  private getPickedPlacementIds(): string[] | [] {
    const pickedPlacements = this.placementCards().filter((placement) => placement.picked);
    if (!pickedPlacements) return [];
    return pickedPlacements.map((placement) => placement.external_id);
  }

  private filterAddedInstances(): Placement[] | [] {
    const pickedPlacements = this.placementCards().filter((placement) => placement.picked) as Placement[];
    return !pickedPlacements ? [] : pickedPlacements;
  }

  private determineValidCreativeLengths(): number[] {
    const potentialCreativeLengths = this.creativeService.getCreativeLengths();
    const longestVideoLength = max(
      this.campaign()
        .targetings.flatMap((t) => t.creatives)
        .map((creative) => creative.length),
    );
    return potentialCreativeLengths.filter((length) => length <= longestVideoLength);
  }

  private buildPlacementCards(): void {
    let placementCards: PlacementCard[] = [];

    this.placements().map((placement) => {
      const start_date = new Date(placement.start_date);
      const end_date = new Date(placement.end_date);
      placementCards = [
        ...placementCards,
        {
          ...placement,
          picked: false,
          start_date: start_date,
          end_date: end_date,
          ad_unit_nodes: placement.ad_unit_nodes.map((ad_unit_node) => {
            return {
              ...ad_unit_node,
              creative_instances: ad_unit_node.creative_instances.map((creative_instance) => {
                const start_date = !creative_instance.start
                  ? new Date(placement.start_date)
                  : new Date(creative_instance.start);
                const end_date = !creative_instance.end
                  ? new Date(placement.end_date)
                  : new Date(creative_instance.end);
                return {
                  ...creative_instance,
                  start: start_date,
                  end: end_date,
                  added: false,
                };
              }),
            };
          }),
        },
      ];
    });

    this.placementCards.set(placementCards);
  }
}

interface PlacementCard extends Placement {
  picked: boolean;
}
